<template>
  <div>
    <div
      ref="searchBarEl"
     
      :class="[
        'relative flex items-center rounded-full text-sm border border-gray-200 cursor-pointer transition-all',
        {
          'bg-white hover:shadow-md': searchFormType === null || searchFormType === 'any',
          'bg-gray-100 w-full max-w-4xl': !isCampgrounds && searchFormType !== null,
          'bg-gray-100 w-full max-w-md': isCampgrounds && searchFormType !== null,
        },
      ]"
      :style="{
        '--search-button-length': `${t('actions.search').length}ch`,
        '--search-bar-button-space': buttonClearance,
      }"
    >
      <SearchFormBarField
        :tag="searchFormType !== null ? 'div' : 'button'"
        test-id="search-location-button"
        :is-open="searchFormType !== null"
        :is-focused="searchFormType === 'location'"
        :is-dimmed="searchFormType === 'any'"
        :show-divider="showDividerAfter('location')"
        :expanded-class="fieldExpandedClass('location', 'flex items-center justify-between gap-2 min-w-0')"
        :collapsed-class="fieldCollapsedClass('location')"
        @click.stop="openSearchForm('location')"
        @mouseenter="handleButtonHover('location')"
        @mouseleave="handleButtonHover()"
      >
        <span class="flex flex-col items-start min-w-0 flex-1">
          <span class="text-xs font-medium text-primary-950">{{ t('searchForm.location.where') }}</span>
          <input
            ref="locationInputEl"
            :value="searchLocation ?? nearbyText"
           
            data-ddid="search-location-input"
            autocomplete="off"
            :placeholder="t('searchForm.location.search')"
            type="text"
            class="w-full min-w-0 outline-none font-medium placeholder:text-primary-950/80 placeholder:font-normal"
            @click.stop
            @focus="openSearchForm('location')"
            @input="(event: Event) => searchLocation = (event.target as HTMLInputElement).value"
            @compositionupdate="({ data }) => searchLocation = data"
            @keyup.enter="handleLocationInputEnter"
          >
        </span>

        <SearchBarClearButton
          v-if="searchFormType === 'location' && searchLocation"
          class="shrink-0"
          @click.stop="clearLocation"
        />

        <template #collapsed>
          {{ isCampgrounds ? collapsedCampgroundsLocationLabel : collapsedRvsLocationLabel }}
        </template>
      </SearchFormBarField>

      <SearchFormBarField
        v-if="!isCampgrounds"
        test-id="search-dates-button"
        :is-open="searchFormType !== null"
        :is-focused="searchFormType === 'dates'"
        :is-dimmed="searchFormType === 'any'"
        :show-divider="showDividerAfter('dates')"
        :show-left-divider="!fieldPosition('dates').isFirst"
        :expanded-class="fieldExpandedClass('dates', 'flex items-center justify-between gap-2')"
        :collapsed-class="fieldCollapsedClass('dates')"
        @click="openSearchForm('dates')"
        @mouseenter="handleButtonHover('dates')"
        @mouseleave="handleButtonHover()"
      >
        <span class="flex flex-col items-start min-w-0 flex-1">
          <span class="text-xs font-medium text-primary-950">{{ t('searchForm.dates.when') }}</span>
          <span
            v-if="searchFormData.dates?.start"
            class="font-medium whitespace-nowrap"
          >
            {{ computedDraftDatesLabel }}
          </span>
          <span
            v-else
            class="text-primary-950/80"
          >
            {{ t('searchForm.dates.add') }}
          </span>
        </span>

        <SearchBarClearButton
          v-if="searchFormType === 'dates' && searchFormData.dates"
          class="shrink-0"
          @click.stop="handleClearDates"
        />
        <template #collapsed>
          {{ collapsedDatesLabel }}
        </template>
      </SearchFormBarField>

      <SearchFormBarField
        v-if="!isCampgrounds"
        test-id="search-guests-button"
        :is-open="searchFormType !== null"
        :is-focused="searchFormType === 'guests'"
        :is-dimmed="searchFormType === 'any'"
        :show-divider="showDividerAfter('guests')"
        :show-left-divider="!fieldPosition('guests').isFirst"
        :expanded-class="fieldExpandedClass('guests', 'flex items-center justify-between gap-2')"
        :collapsed-class="fieldCollapsedClass('guests')"
        @click="openSearchForm('guests')"
        @mouseenter="handleButtonHover('guests')"
        @mouseleave="handleButtonHover()"
      >
        <span class="flex flex-col items-start min-w-0 flex-1">
          <span class="text-xs font-medium text-primary-950">{{ t('searchForm.guests.who') }}</span>
          <span class="truncate">
            <span
              v-if="searchFormData.guests?.adults || searchFormData.guests?.children || searchFormData.guests?.petFriendly"
              class="font-medium"
            >
              {{ computedDraftGuestsLabel }}
            </span>
            <span
              v-else
              class="text-primary-950/80"
            >
              {{ t('searchForm.guests.add') }}
            </span>
          </span>
        </span>

        <SearchBarClearButton
          v-if="searchFormType === 'guests' && searchFormData.guests"
          class="shrink-0"
          @click.stop="handleClearGuests"
        />
        <template #collapsed>
          {{ collapsedGuestsLabel }}
        </template>
      </SearchFormBarField>

      <template v-if="hasRentalType && !isCampgrounds">
        <SearchFormBarField
          test-id="search-rental-type-button"
          :is-open="searchFormType !== null"
          :is-focused="searchFormType === 'rentalType'"
          :show-left-divider="!fieldPosition('rentalType').isFirst"
          :expanded-class="fieldExpandedClass('rentalType', 'flex flex-col items-start')"
          :collapsed-class="fieldCollapsedClass('rentalType')"
          @click="openSearchForm('rentalType')"
          @mouseenter="handleButtonHover('rentalType')"
          @mouseleave="handleButtonHover()"
        >
          <span class="text-xs font-medium text-primary-950">{{ t('searchForm.rentalType.title') }}</span>
          <span
            v-if="searchFormData.rentalType"
            class="font-medium truncate"
          >
            {{ computedDraftRentalTypeLabel }}
          </span>
          <span
            v-else
            class="text-primary-950/80"
          >
            {{ t('searchForm.rentalType.allRVs') }}
          </span>
          <template #collapsed>
            {{ collapsedRentalTypeLabel }}
          </template>
        </SearchFormBarField>
      </template>

      <button
        type="button"
       
        data-ddid="search-button"
        :class="[
          'absolute right-0 flex items-center justify-center min-w-8 rounded-full bg-secondary text-white mr-2 cursor-pointer',
          searchButtonTransitionClass,
          searchFormType === null ? 'h-8 px-2' : 'h-12 px-4',
          showSearchLabel ? 'gap-2' : 'gap-0',
        ]"
        @click="handleTriggerSearch"
      >
        <TIcon
          :icon="['far', 'magnifying-glass']"
          class="shrink-0"
        />

        <span
          :class="[
            'overflow-hidden whitespace-nowrap text-base font-medium',
            searchLabelTransitionClass,
            showSearchLabel ? 'opacity-100 max-w-[calc(var(--search-button-length)+4px)]' : 'opacity-0 max-w-0',
          ]"
        >{{ t('actions.search') }}</span>
      </button>

      <ClientOnly>
        <AtomTransition
          v-show="searchFormType !== null"
          timing="fast"
          variant="slide-down"
        >
          <div
            v-if="searchFormType !== 'any'"
            class="absolute top-[calc(100%+0.5rem)] z-40 bg-white text-base rounded-xl ring-1 ring-gray-100 shadow-xl p-4 transition-[width,left] duration-300 ease-in-out overflow-hidden cursor-default"
            :style="panelStyle"
          >
            <template v-if="searchFormType === 'location'">
              <TOverlay :show="isPredictionsLoading">
                <LazyLocationPredictions
                  v-if="predictions.length"
                  :predictions="predictions"
                  @set-place="handleLocationPredictionClick"
                />
                <SearchBarLocationSuggestions
                  v-else
                  :nearby-text="nearbyText"
                  @nearby="handleNearbySearch"
                  @select="handleSuggestedLocationSearch"
                />
              </TOverlay>
            </template>

            <template v-if="searchFormType === 'dates'">
              <LazySearchFormFieldDates
                :dates="searchFormData.dates ?? {}"
                :disabled-dates="disabledDates"
                @update:dates="handleDatesChange"
              />

              <BannersSeasonal
                v-if="showThanksGivingBanner"
                class="mt-4"
              />
            </template>

            <template v-if="searchFormType === 'guests'">
              <LazySearchFormFieldGuests
                :model-value="searchFormData.guests"
                @update:model-value="handleGuestsChange"
              />
            </template>

            <template v-if="searchFormType === 'rentalType'">
              <LazySearchFormFieldRentalType
                :model-value="searchFormData.rentalType"
                @update:model-value="handleRentalTypeChange"
              />
            </template>
          </div>
        </AtomTransition>
      </ClientOnly>
    </div>

    <AtomTransition timing="fast">
      <div
        v-if="searchFormType !== null && isToggleable"
        class="absolute w-full h-dvh z-30 inset-0 pointer-events-all top-full bg-black/20"
        @click="handleCloseSearchForm()"
      />
    </AtomTransition>
  </div>
</template>

<script setup lang="ts">
import type { SearchDates, SearchFormType, SearchGuests, SearchLocation, SearchRentalType } from '~/types'

const props = defineProps<{
  isToggleable?: boolean
  hasRentalType?: boolean
  isCampgrounds?: boolean
}>()

const emit = defineEmits<{
  searchFormVisibilityChange: [value: boolean]
}>()

const { t } = useI18n()
const router = useRouter()
const { showThanksGivingBanner } = useExperiment()

const searchData = useSearchData()
const {
  collapsedCampgroundsLocationLabel,
  collapsedRvsLocationLabel,
  collapsedDatesLabel,
  collapsedGuestsLabel,
  collapsedRentalTypeLabel,
} = useSearch()

const {
  computedDraftDatesLabel,
  computedDraftGuestsLabel,
  computedDraftRentalTypeLabel,

  searchFormData,
  disabledDates,
  nearbyText,
  updateSearchFormLocation,
  clearSearchFormLocation,
  updateSearchFormDates,
  clearSearchFormDates,
  updateSearchFormGuests,
  clearSearchFormGuests,
  updateSearchFormRentalType,
  submitCampgroundsSearchForm,
  submitSearchForm,
} = useSearchForm()

const {
  searchLocation,
  predictions,
  isPredictionsLoading,
  clearLocationPredictions,
  ignoreSearchLocationUpdates,
  prePopulateSearchLocation,
} = useLocationPredictions()

const searchFormType = ref<Nullable<SearchFormType>>(props.isToggleable ? null : 'any')
const hoveredButton = ref<SearchFormType | undefined>()

const visibleFieldKeys = computed<SearchFormType[]>(() => {
  if (props.isCampgrounds) {
    return ['location']
  }

  return props.hasRentalType
    ? ['location', 'dates', 'guests', 'rentalType']
    : ['location', 'dates', 'guests']
})

function fieldPosition(key: SearchFormType) {
  const fields = visibleFieldKeys.value
  const index = fields.indexOf(key)

  return {
    index,
    count: fields.length,
    isFirst: index === 0,
    isLast: index === fields.length - 1,
  }
}

function fieldExpandedClass(key: SearchFormType, base: string) {
  const { isLast } = fieldPosition(key)

  return [
    base,
    'flex-1 pl-6',
    isLast ? 'pr-(--search-bar-button-space)' : 'pr-2',
  ]
}

function fieldCollapsedClass(key: SearchFormType) {
  const { isLast } = fieldPosition(key)

  return isLast ? 'pl-4 pr-(--search-bar-button-space)' : 'px-4'
}

function handleButtonHover(type?: SearchFormType) {
  hoveredButton.value = type
}

function showDividerAfter(key: SearchFormType) {
  const fields = visibleFieldKeys.value
  const next = fields[fields.indexOf(key) + 1]
  if (!next) {
    return false
  }

  function isActive(k: SearchFormType) {
    return searchFormType.value === k || hoveredButton.value === k
  }

  return !isActive(key) && !isActive(next)
}

const locationInputEl = useTemplateRef('locationInputEl')

function applySearchFormLocation(location?: Partial<SearchLocation>) {
  if (location?.placeId) {
    updateSearchFormLocation({
      placeId: location.placeId,
      text: location?.text ?? '',
    })
  }
  else {
    clearSearchFormLocation()
  }

  if (!location) {
    // Explicit clear: nothing is left to predict from, so reset silently.
    clearLocationPredictions()
    ignoreSearchLocationUpdates(() => {
      searchLocation.value = ''
    })
    return
  }

  // A selection (prediction, nearby, or suggested destination) sets the text like a
  // normal edit, so predictions refresh to match what was just picked instead of
  // staying empty or holding onto whatever was showing before the pick.
  searchLocation.value = location.text ?? ''
}

async function openSearchForm(type: SearchFormType) {
  if (searchFormType.value === type && type !== 'location') {
    locationInputEl.value?.blur()
    searchFormType.value = 'any'
    return
  }

  searchFormType.value = type

  if (type === 'location') {
    if (searchLocation.value === nearbyText.value) {
      clearLocation()
    }

    await nextTick(() => {
      locationInputEl.value?.focus()
    })
  }
}

const searchBarEl = useTemplateRef('searchBarEl')

const showSearchLabel = computed(() => searchFormType.value !== null && searchFormType.value !== 'any')

const searchButtonTransitionClass = computed(() => {
  if (searchFormType.value === null) {
    return 'transition-none'
  }
  return 'transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
})
const searchLabelTransitionClass = computed(() => {
  if (searchFormType.value === null) {
    return 'transition-none'
  }
  return 'transition-[max-width,opacity] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
})

// The search button is absolutely positioned, so the last field reserves room for it. Beyond the
// label, the button spans 4rem of icon, gap, horizontal padding and right offset. The remaining
// rem keeps the field's clear button from landing underneath it. This stays constant while open
// so the field width does not shift when the label appears.
const buttonClearance = computed(() => {
  const openButtonClearance = `calc(var(--search-button-length, ${t('actions.search').length}ch) + 5rem)`

  if (!props.isToggleable) {
    return openButtonClearance
  }

  if (searchFormType.value === null) {
    return 'calc(2rem + 20px)'
  }

  return openButtonClearance
})

/**
 * Width fractions for the search form panels.
 * EX: 0.5 means 50% of the width of the search bar.
 */
const panelWidthFraction: Record<SearchFormType, number> = {
  any: 0,
  dates: 1,
  guests: 1 / 3,
  location: 1,
  rentalType: 0.5,
}

const panelStyle = computed(() => {
  if (searchFormType.value === null || searchFormType.value === 'any') {
    return {}
  }

  const widthFraction = searchFormType.value === 'location' && !props.isCampgrounds
    ? 0.5
    : panelWidthFraction[searchFormType.value]
  const { index, count } = fieldPosition(searchFormType.value)
  let left = Math.min(index / count, 1 - widthFraction)

  if (widthFraction < 0.5) {
    left = left - 0.1
  }

  return {
    width: `${widthFraction * 100}%`,
    left: `${left * 100}%`,
  }
})

onClickOutside(searchBarEl, () => {
  if (searchFormType.value !== null) {
    searchFormType.value = 'any'
  }
})

function handleLocationChange(location?: SearchLocation) {
  applySearchFormLocation(location)

  if (location?.text) {
    triggerAutoAdvance()
  }
}

function handleLocationInputEnter() {
  if (predictions.value?.[0]) {
    handleLocationPredictionClick(predictions.value[0])
    locationInputEl.value?.blur?.()
  }
}

function handleLocationPredictionClick(place: google.maps.places.PlacePrediction) {
  handleLocationChange({
    placeId: place.placeId,
    text: place.mainText?.text ?? '',
  })
}

function handleNearbySearch() {
  handleLocationChange({ placeId: '', text: nearbyText.value })
}

function handleSuggestedLocationSearch(location: SearchLocation) {
  handleLocationChange(location)
}

function clearLocation() {
  applySearchFormLocation()
}

// Mirror applied location into the predictions input. On POI pages the form draft may
// hold a pre-populated place while applied search stays location-less.
watch(() => searchData.value.location, () => {
  clearLocationPredictions()
  prePopulateSearchLocation(searchData.value.location?.name ?? searchFormData.value.location?.text ?? '')
}, { immediate: true })

function handleDatesChange({ start, end }: SearchDates) {
  updateSearchFormDates({
    start,
    end,
  })

  if (start && end) {
    triggerAutoAdvance()
  }
}

function handleClearDates() {
  clearSearchFormDates()
}

function handleGuestsChange(guests?: SearchGuests) {
  updateSearchFormGuests(guests)
}

function handleClearGuests() {
  clearSearchFormGuests()
}

function handleRentalTypeChange(rentalType?: SearchRentalType) {
  updateSearchFormRentalType(rentalType)
}

function triggerAutoAdvance() {
  if (props.isCampgrounds) {
    openSearchForm('any')
    return
  }

  if (!searchFormData.value.dates) {
    openSearchForm('dates')
    return
  }

  if (!searchFormData.value.guests) {
    openSearchForm('guests')
    return
  }

  // Always open the dates form when the location is selected
  if (searchFormType.value === 'location') {
    openSearchForm('dates')
  }
}

function resetSearchFormType() {
  if (props.isToggleable) {
    searchFormType.value = null
  }
  else {
    searchFormType.value = 'any'
  }
}

async function handleTriggerSearch() {
  if (searchFormType.value === null) {
    openSearchForm('any')
    return
  }

  if (props.isCampgrounds) {
    await submitCampgroundsSearchForm()
    resetSearchFormType()
    return
  }

  await submitSearchForm()

  resetSearchFormType()
}

watch(router.currentRoute, () => {
  resetSearchFormType()
})

watch(searchFormType, () => {
  emit('searchFormVisibilityChange', searchFormType.value !== null)
})

function handleCloseSearchForm() {
  resetSearchFormType()
}
</script>

<i18n src="~~/i18n/locales/common/search/form.json" lang="json" />
